import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Sparkles, HelpCircle, Check } from 'lucide-react';
import { type SearchItemResult, type LostItem, TeamAApi, ApiError } from '@ai-tiaozs/shared';
import { SafeImage } from '@/components/SafeImage';

/**
 * A 队 · ② 智能寻找结果(肖淑慧)
 * 信任型自助:不替用户判定"属于你",只给候选和固定存放地点,
 * 由失主自己到失物点核对。
 */
export function TeamACandidates() {
  const navigate = useNavigate();
  const [result, setResult] = useState<SearchItemResult | null>(null);
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const raw = sessionStorage.getItem('ab_search_result');
    if (raw) setResult(JSON.parse(raw));
  }, []);

  if (!result) {
    return (
      <div className="card text-sm text-slate-500">
        暂无候选结果,先去
        <Link to="/search" className="ml-1 text-teamA-primary underline">智能寻找</Link>
        试试。
      </div>
    );
  }

  const reportFeedback = async (item: LostItem, feedback: 'not_found' | 'not_mine') => {
    setFeedbackId(item.id);
    setError(null);
    try {
      await TeamAApi.feedback({
        searchId: result.searchId,
        selectedItemId: item.id,
        feedback,
      });
      // 反馈后回首页
      navigate('/');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '反馈失败');
    } finally {
      setFeedbackId(null);
    }
  };

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-lg font-bold">② AI 候选匹配</h1>
        <Link to="/search" className="text-xs text-teamA-primary underline">← 重新寻找</Link>
      </header>

      {/* 系统诚信提示 */}
      <div className="rounded-md border border-teamA-primary/20 bg-teamA-primary/5 px-3 py-2 text-[11px] text-slate-700">
        <p>以下是可能符合描述的物品,请到对应失物存放点自行核对,只领取属于自己的物品。</p>
      </div>

      {result.uncertain && (
        <div className="rounded-md bg-teamA-warning/10 px-3 py-2 text-xs text-teamA-warning">
          {result.uncertain}
        </div>
      )}

      {/* AI 主动追问 */}
      {result.needsMoreDetail && result.followUpQuestions.length > 0 && (
        <div className="card border-teamA-warning/30 bg-teamA-warning/5 space-y-2">
          <h2 className="flex items-center gap-1 text-sm font-semibold text-teamA-warning">
            <HelpCircle className="h-4 w-4" /> AI 主动追问
          </h2>
          <p className="text-xs text-slate-600">失物墙中有多个相似物品,AI 暂无法直接认定。请补充:</p>
          <ul className="list-inside list-disc text-xs text-slate-700">
            {result.followUpQuestions.map((q, i) => <li key={i}>{q}</li>)}
          </ul>
          <Link to="/search" className="btn-outline w-full text-xs">
            补充细节后重新寻找
          </Link>
        </div>
      )}

      {result.candidates.length === 0 ? (
        <div className="card text-sm text-slate-500">
          失物库中没有特别相似的物品。你可以补充描述后再试,或先去失物墙浏览。
        </div>
      ) : (
        <div className="space-y-3">
          {result.candidates.map((c, i) => (
            <section key={c.item.id} className="card">
              <div className="flex items-start gap-3">
                <SafeImage
                  team="a"
                  imageUrl={c.item.imageUrl}
                  imageKey={c.item.imageKey}
                  alt={c.item.title}
                />
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-1">
                    <span className="badge bg-teamA-primary/10 text-teamA-primary">第 {i + 1} 候选</span>
                    <span className="badge bg-slate-200 text-slate-700">{c.item.category}</span>
                    <span className="text-[11px] text-slate-500">相似度 {(c.score * 100).toFixed(0)}%</span>
                  </div>
                  <p className="mt-1 text-sm font-semibold">{c.item.title}</p>
                  {c.item.foundLocation && (
                    <p className="text-[11px] text-slate-500">捡到于 {c.item.foundLocation}</p>
                  )}
                  {/* 固定存放地点 — 醒目 */}
                  {c.item.storageLocation && (
                    <p className="mt-1 rounded-md bg-teamA-primary/5 px-2 py-1 text-xs">
                      <MapPin className="mr-1 inline h-3 w-3 text-teamA-primary" />
                      <span className="text-slate-500">领取地点</span>
                      <span className="ml-1 font-semibold text-teamA-primary">{c.item.storageLocation}</span>
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-2 rounded-md bg-slate-50 p-2 text-xs">
                <p className="font-semibold text-slate-700">AI 匹配理由:</p>
                {c.matchedFeatures.length > 0 ? (
                  <ul className="mt-1 list-inside list-disc text-slate-600">
                    {c.matchedFeatures.map((m, idx) => <li key={idx}>{m}</li>)}
                  </ul>
                ) : (
                  <p className="text-slate-500">无明显命中</p>
                )}
                {c.difference && <p className="mt-1 text-slate-500">差异:{c.difference}</p>}
              </div>

              {/* 两个动作按钮:查看位置 / 我找到了 */}
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Link
                  to={`/pickup/${c.item.id}`}
                  className="btn-outline text-center"
                >
                  <MapPin className="mr-1 inline h-4 w-4" /> 查看领取位置
                </Link>
                <button
                  onClick={() => navigate(`/pickup/${c.item.id}`)}
                  disabled={feedbackId === c.item.id}
                  className="btn-primary"
                >
                  <Check className="mr-1 h-4 w-4" /> 我找到的是这一件
                </button>
              </div>
              {/* 反馈按钮 */}
              <div className="mt-2 flex justify-end gap-2 text-[11px]">
                <button
                  onClick={() => reportFeedback(c.item, 'not_found')}
                  disabled={feedbackId === c.item.id}
                  className="text-slate-400 hover:text-teamA-danger"
                >
                  没找到
                </button>
                <span className="text-slate-300">·</span>
                <button
                  onClick={() => reportFeedback(c.item, 'not_mine')}
                  disabled={feedbackId === c.item.id}
                  className="text-slate-400 hover:text-teamA-danger"
                >
                  不是我的
                </button>
              </div>
            </section>
          ))}
        </div>
      )}

      {error && <p className="text-xs text-teamA-danger">{error}</p>}

      <p className="flex items-center gap-1 text-[11px] text-slate-500">
        <Sparkles className="h-3 w-3" /> 找到类似物品后,请到失物点实地核对,只领取自己的物品。
      </p>

      <Link to="/" className="btn-outline w-full">
        <ArrowLeft className="mr-1 h-4 w-4" /> 返回失物墙
      </Link>
    </div>
  );
}
